import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * ReferensGallery — sammanhållet premiumgalleri för utförda arbeten.
 *
 * Design:
 *   - EN stor huvudbild i fokus, nästa bild anas delvis till höger på ≥lg
 *   - Diskreta pil-knappar (vänster/höger) samt indikator "n / total"
 *   - Touch/pointer-swipe på mobil, ingen autoplay
 *   - Klick på huvudbilden öppnar en enkel lightbox (contain), navigering
 *     via ← → och Escape samt klick utanför bildytan
 *   - Endast mjuk slide-övergång via CSS transform
 *
 * Inga externa dependencies.
 */
export default function ReferensGallery({ images }) {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = images.length;

  const go = useCallback(
    (delta) => {
      setIndex((i) => (i + delta + total) % total);
    },
    [total],
  );
  const goTo = useCallback(
    (n) => setIndex(((n % total) + total) % total),
    [total],
  );

  return (
    <>
      <Carousel
        images={images}
        index={index}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onGoTo={goTo}
        onOpen={() => setLightboxOpen(true)}
      />
      {lightboxOpen && (
        <Lightbox
          images={images}
          index={index}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
        />
      )}
    </>
  );
}

/* ---------- Carousel ------------------------------------------------------ */

function Carousel({ images, index, onPrev, onNext, onGoTo, onOpen }) {
  const total = images.length;
  const trackRef = useRef(null);
  const dragState = useRef({ startX: 0, dx: 0, dragging: false, pointerId: 0 });

  const onPointerDown = (e) => {
    // Bara primär pointer (musknapp/touch).
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const t = trackRef.current;
    if (!t) return;
    // OBS: vi använder INTE setPointerCapture — det gör att `click`-eventet
    // på slide-knappen (som öppnar lightboxen) inte längre levereras.
    dragState.current = {
      startX: e.clientX,
      dx: 0,
      dragging: true,
      pointerId: e.pointerId,
    };
    t.style.transition = "none";
  };
  const onPointerMove = (e) => {
    const st = dragState.current;
    if (!st.dragging) return;
    st.dx = e.clientX - st.startX;
    const t = trackRef.current;
    if (t) {
      // Vi flyttar sliden temporärt med musen.
      t.style.transform = `translate3d(calc(${-index * 100}% + ${st.dx}px), 0, 0)`;
    }
  };
  const endDrag = () => {
    const st = dragState.current;
    if (!st.dragging) return;
    st.dragging = false;
    const t = trackRef.current;
    if (t) t.style.transition = "";
    const threshold = 60; // px innan vi räknar som swipe
    const dxAbs = Math.abs(st.dx);
    if (st.dx <= -threshold) {
      onNext();
    } else if (st.dx >= threshold) {
      onPrev();
    } else if (t) {
      // Snap tillbaka
      t.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
    }
    // Behåll info så onClickCapture kan svälja klicket om det var en drag.
    dragState.current = { ...st, wasDrag: dxAbs > 5, dx: 0 };
  };

  // Om vi just draggade — svälj det syntetiska click som annars öppnar lightbox.
  const onClickCapture = (e) => {
    if (dragState.current.wasDrag) {
      e.stopPropagation();
      e.preventDefault();
      dragState.current.wasDrag = false;
    }
  };

  // Kör om transform vid ny index (efter att drag släppts).
  useEffect(() => {
    const t = trackRef.current;
    if (t && !dragState.current.dragging) {
      t.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
    }
  }, [index]);

  return (
    <div
      data-testid="ref-carousel"
      className="relative select-none"
      aria-roledescription="carousel"
      aria-label="Utförda arbeten"
    >
      {/*
        Viewport: på desktop begränsar vi vyn till ~86 % av bredden så att
        nästa bild anas till höger — men vi centrerar viewporten så
        huvudbilden hamnar mitt i containern.
      */}
      <div className="mx-auto">
        <div
          className="overflow-hidden rounded-2xl bg-[#E2E0D8]"
          style={{ touchAction: "pan-y" }}
        >
          <div
            className="lg:max-w-[92%] lg:mx-auto"
            style={{ position: "relative" }}
          >
            <div
              ref={trackRef}
              className="flex transition-transform duration-500 ease-out will-change-transform"
              style={{
                transform: `translate3d(${-index * 100}%, 0, 0)`,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerLeave={endDrag}
              onClickCapture={onClickCapture}
            >
              {images.map((img, i) => (
                <Slide
                  key={img.src}
                  img={img}
                  active={i === index}
                  onOpen={onOpen}
                  eager={i === 0}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pilar */}
      <button
        type="button"
        onClick={onPrev}
        data-testid="ref-carousel-prev"
        aria-label="Föregående bild"
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[#1B2A1E] shadow-md border border-[#E2E0D8] transition-colors"
      >
        <ChevronLeft className="w-6 h-6" strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={onNext}
        data-testid="ref-carousel-next"
        aria-label="Nästa bild"
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[#1B2A1E] shadow-md border border-[#E2E0D8] transition-colors"
      >
        <ChevronRight className="w-6 h-6" strokeWidth={2} />
      </button>

      {/* Fot: positionsindikator + dots */}
      <div className="mt-4 flex items-center justify-between gap-4">
        <div
          data-testid="ref-carousel-index"
          className="text-xs font-semibold text-[#5A6258] tracking-wide"
          aria-live="polite"
        >
          <span className="text-[#1B2A1E] font-extrabold">{index + 1}</span>
          <span className="mx-1">/</span>
          <span>{total}</span>
        </div>
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onGoTo(i)}
              data-testid={`ref-carousel-dot-${i}`}
              aria-label={`Gå till bild ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index
                  ? "w-6 bg-[#D95D2E]"
                  : "w-1.5 bg-[#C9C6BC] hover:bg-[#5A6258]"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Slide({ img, active, onOpen, eager }) {
  return (
    <div className="shrink-0 basis-full min-w-0 px-1 sm:px-2">
      <button
        type="button"
        onClick={active ? onOpen : undefined}
        tabIndex={active ? 0 : -1}
        data-testid={active ? "ref-carousel-active-slide" : undefined}
        className={`group relative block w-full overflow-hidden rounded-xl bg-[#E2E0D8] ${
          active ? "cursor-zoom-in" : "cursor-default"
        }`}
        style={{ aspectRatio: "16 / 10" }}
        aria-label={`Öppna större bild: ${img.alt}`}
      >
        <img
          src={img.src}
          alt={img.alt}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          style={img.objectPosition ? { objectPosition: img.objectPosition } : undefined}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
        />
        {/* Diskret bildtext + hint om att klicka öppnar större */}
        {img.caption && (
          <span className="absolute left-3 bottom-3 inline-flex items-center rounded-md bg-black/45 text-white text-xs font-medium px-2 py-1 backdrop-blur-sm">
            {img.caption}
          </span>
        )}
      </button>
    </div>
  );
}

/* ---------- Lightbox ------------------------------------------------------ */

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const img = images[index];
  const backdropRef = useRef(null);

  // Tangentbord: ← → Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  // Låser body-scroll medan lightboxen är öppen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const onBackdropClick = (e) => {
    // Klick utanför bildytan stänger.
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      onClick={onBackdropClick}
      data-testid="ref-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Bildvisning"
      className="fixed inset-0 z-[60] bg-[#0b120d]/92 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="ref-lightbox-close"
        aria-label="Stäng bildvisning"
        className="absolute top-4 right-4 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      <button
        type="button"
        onClick={onPrev}
        data-testid="ref-lightbox-prev"
        aria-label="Föregående bild"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <ChevronLeft className="w-7 h-7" />
      </button>
      <button
        type="button"
        onClick={onNext}
        data-testid="ref-lightbox-next"
        aria-label="Nästa bild"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <ChevronRight className="w-7 h-7" />
      </button>

      <figure className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center">
        <img
          key={img.src}
          src={img.src}
          alt={img.alt}
          className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
          decoding="async"
          draggable={false}
        />
        <figcaption className="mt-3 text-xs text-white/75 tracking-wide">
          <span className="font-semibold text-white">{index + 1}</span>
          <span className="mx-1">/</span>
          <span>{images.length}</span>
          {img.caption ? <span className="ml-3">{img.caption}</span> : null}
        </figcaption>
      </figure>
    </div>
  );
}
